import React from "react";
import { ChartDonut } from "@phosphor-icons/react";
import { adaFromLovelace } from "@/lib/governance";

const COLORS = {
  active: "#22d3ee",
  inactive: "#a855f7",
  abstain: "#e879f9",
  noconf: "#f87171",
};

const Donut = ({ parts, total, activePct }) => {
  const R = 60;
  const SW = 15;
  const C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div className="relative shrink-0" style={{ width: 150, height: 150 }}>
      <svg width={150} height={150} viewBox="0 0 150 150">
        <circle cx={75} cy={75} r={R} fill="none" stroke="rgba(148,163,184,0.12)" strokeWidth={SW} />
        <g transform="rotate(-90 75 75)">
          {parts.map((p) => {
            const frac = total ? Math.max(0, Number(p.value) / total) : 0;
            const seg = (
              <circle
                key={p.key}
                cx={75}
                cy={75}
                r={R}
                fill="none"
                stroke={p.color}
                strokeWidth={SW}
                strokeDasharray={`${frac * C} ${C - frac * C}`}
                strokeDashoffset={-acc * C}
              />
            );
            acc += frac;
            return seg;
          })}
        </g>
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div className="font-heading text-[22px] leading-none text-[#22d3ee] text-glow-cyan tabnum">{activePct}%</div>
          <div className="text-[9px] uppercase tracking-[0.22em] mono text-muted-foreground mt-1.5">Active</div>
        </div>
      </div>
    </div>
  );
};

// Option 1 — modern glass card body for DRep voting power:
// breakdown list (left) + donut chart with legend (right).
export const DrepPowerCard = ({ d }) => {
  const drep = d?.drep || {};
  const total = Number(drep.total || 0);
  const pct = (v) => (total ? (Number(v) / total) * 100 : 0);
  const fmtPct = (v) => pct(v).toFixed(1);

  const parts = [
    { key: "active", label: "Active", short: "Active", value: drep.active, color: COLORS.active },
    { key: "inactive", label: "Inactive", short: "Inactive", value: drep.inactive, color: COLORS.inactive },
    { key: "abstain", label: "Always Abstain", short: "Abstain", value: drep.always_abstain, color: COLORS.abstain },
    { key: "noconf", label: "Always No-Confidence", short: "No Confidence", value: drep.always_no_confidence, color: COLORS.noconf },
  ];

  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-10">
      {/* breakdown */}
      <div className="min-w-0 lg:flex-1 space-y-2.5">
        {parts.map((p) => (
          <div key={p.key} className="flex items-center justify-between gap-3" data-testid={`drep-${p.key}`}>
            <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.1em] mono text-muted-foreground">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: p.color }} />
              {p.label}
            </span>
            <span className="text-[12px] mono tabnum text-foreground text-right lg:whitespace-nowrap">{adaFromLovelace(p.value)}</span>
          </div>
        ))}
        <div className="h-px bg-primary/15 my-1" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] uppercase tracking-[0.1em] mono text-[#22d3ee]">Total</span>
          <span className="text-[13px] mono tabnum text-foreground text-glow-cyan text-right lg:whitespace-nowrap">{adaFromLovelace(drep.total)}</span>
        </div>
      </div>

      {/* donut + legend */}
      <div className="flex items-center justify-center gap-5 shrink-0">
        <Donut parts={parts} total={total} activePct={fmtPct(drep.active)} />
        <div className="space-y-2.5">
          {parts.map((p) => (
            <div key={p.key} className="flex items-center gap-2.5 text-[11px] mono">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: p.color }} />
              <span className="text-muted-foreground w-[92px]">{p.short}</span>
              <span className="text-foreground tabnum w-[42px] text-right">{fmtPct(p.value)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
